import { VStack, Box, Text, Image, Skeleton, Spinner, Button } from '@chakra-ui/react';
import MerchantLogo from './MerchantLogo';
import PalommaDisclaimer from './svg/PalommaDisclaimer';
import approved from './../assets/aprobado.png';
import failed from './../assets/fallido.png';
import { useTRPC } from '../trpc/client';
import useQueryStringParams from '../hooks/useQueryStringParams';
import { useEffect, useContext } from 'react';
import MerchantContext from '../contexts/MerchantContext';
import { statusMessagesMapping } from '../constants/paymentStatusMessages';
import useCurrencyFormatter from '../hooks/useCurrencyFormatter';
import { INVOICE_STATUS } from '../constants/enums';
import _ from 'lodash';
import { now } from '../common/time';
import { BrebPaymentAwait } from './BrebPaymentAwait';
import { BancolombiaCashPaymentAwait } from './BancolombiaCashPaymentAwait';
import { useQuery } from '@tanstack/react-query';
import { invoiceReference } from '../types';

type Props = { txnId?: string[] };

const Receipt = ({ txnId }: Props): JSX.Element => {
  const trpc = useTRPC();
  const { id: queryId } = useQueryStringParams(['id']);
  const ids = txnId || queryId?.split(',');
  const { merchant } = useContext(MerchantContext);
  const formatCurrency = useCurrencyFormatter();

  const queryOptions = trpc.invoices.get.queryOptions({
    rentalsMerchantId: merchant?.id ?? '',
    ids: ids || [],
  });

  const invoiceRequest = useQuery({
    ...queryOptions,
    enabled: !!ids,
    initialData: [],
    refetchOnWindowFocus: false,
  });
  const invoices = invoiceRequest.data;

  useEffect(() => {
    if (invoices?.some((invoice) => invoice.status === INVOICE_STATUS.READY && invoice.paymentDate)) {
      const intervalId = setInterval(() => {
        invoiceRequest.refetch();
      }, 3000);

      return () => clearInterval(intervalId);
    }
  }, [invoiceRequest, invoices]);

  const icon = invoices[0]?.status === INVOICE_STATUS.PAID ? approved : failed;

  const isCash = invoices[0]?.paymentMethod === 'cash';

  const isDeclined =
    invoices[0]?.status !== INVOICE_STATUS.PAID &&
    !(invoices[0]?.status === INVOICE_STATUS.READY && invoices[0]?.paymentDate);

  const approved1 =
    invoices[0]?.status === INVOICE_STATUS.PAID
      ? '¡PAGO APROBADO!'
      : invoices[0]?.status === INVOICE_STATUS.READY && invoices[0]?.paymentDate
      ? 'PAGO EN PROCESO'
      : '¡PAGO CANCELADO!';
  const approved2 =
    invoices[0]?.status === INVOICE_STATUS.PAID
      ? '¡Recibimos tu pago satisfactoriamente!'
      : invoices[0]?.status === INVOICE_STATUS.READY &&
        invoices[0]?.paymentDate &&
        invoices[0]?.paymentMethod !== 'breb'
      ? `Tu pago está en proceso. ${
          invoices[0]?.paymentMethod === 'nequiButton'
            ? 'Complétalo en tu aplicación Nequi. Podemos tardarnos unos minutos en confirmarte.'
            : 'Espera unos momentos mientras confirmamos tu pago.'
        } Cerrar esta ventana no afectará tu pago.`
      : '';

  const paymentMethodMap = {
    card: 'Tarjeta de Crédito',
    pse: 'PSE',
    nequiButton: 'Botón Nequi',
    bancolombiaButton: 'Botón Bancolombia',
    breb: 'Bre-B',
    cash: 'Efectivo Bancolombia',
  };

  const totalPalommaPlusRewardAmount = _.sumBy(invoices, (i) => i.palommaPlusRewardAmount || 0);
  const hasPalommaPlusWin = totalPalommaPlusRewardAmount > 0;

  const description = invoices.map((invoice) => invoice.description).join(', ');
  const totalPaidAmount = _.sumBy(invoices, (ele) => ele.paymentAmount || 0);
  const totalSubscriptionAmount = _.sumBy(invoices, (i) => i.subscriptionAmount || 0);
  const totalCreditsAmount = _.sumBy(invoices, (i) => i.palommaCreditsAmount || 0);
  const additionalCharge =
    totalSubscriptionAmount > 0 && invoices[0]?.paymentMethod === 'card'
      ? Math.round((totalPaidAmount + totalSubscriptionAmount) * ((invoices[0]?.cardFeePercentage ?? 0) * 0.01))
      : _.sumBy(invoices, (ele) => ele.cardFee || 0);

  const totalAmount = Number(totalPaidAmount || invoices[0]?.amount || 0) + totalSubscriptionAmount + additionalCharge;

  const tableFields = [
    invoices[0]?.customerName ? { fieldName: 'Arrendatario (a):', fieldValue: invoices[0]?.customerName } : null,
    { fieldName: 'Descripción del pago', fieldValue: description },
    {
      fieldName: 'N. referencia',
      fieldValue: invoices.length ? invoices.map(invoiceReference).join(',') : '',
    },
    invoices[0]?.paymentMethod
      ? { fieldName: 'Método de pago', fieldValue: paymentMethodMap[invoices[0]?.paymentMethod] }
      : null,

    {
      fieldName: 'Estado',
      fieldValue: invoices[0]?.status === INVOICE_STATUS.PAID ? 'Aprobado' : isDeclined ? 'Rechazado' : 'Procesando',
    },

    invoices[0]?.paymentMethod === 'card'
      ? [
          { fieldName: 'Valor', fieldValue: formatCurrency(totalPaidAmount || invoices[0]?.amount || 0) },
          ...(totalCreditsAmount > 0
            ? [{ fieldName: 'Saldo Palomma', fieldValue: `-${formatCurrency(totalCreditsAmount)}` }]
            : []),
          ...(totalSubscriptionAmount > 0
            ? [{ fieldName: 'Palomma Plus', fieldValue: formatCurrency(totalSubscriptionAmount) }]
            : []),
          {
            fieldName: `Sobrecostos (${invoices[0]?.cardFeePercentage}%)`,
            fieldValue: formatCurrency(additionalCharge),
          },
        ]
      : totalSubscriptionAmount > 0 || totalCreditsAmount > 0
      ? [
          { fieldName: 'Valor', fieldValue: formatCurrency(totalPaidAmount || invoices[0]?.amount || 0) },
          ...(totalCreditsAmount > 0
            ? [{ fieldName: 'Saldo Palomma', fieldValue: `-${formatCurrency(totalCreditsAmount)}` }]
            : []),
          { fieldName: 'Palomma Plus', fieldValue: formatCurrency(totalSubscriptionAmount) },
        ]
      : null,

    {
      fieldName: 'Valor total',
      fieldValue: formatCurrency(totalAmount),
    },
  ]
    .flat()
    .filter(Boolean);

  return (
    <Skeleton isLoaded={!!invoiceRequest.data.length}>
      <Box
        width={'100%'}
        maxW={{ base: '100%', md: '600px' }}
        mx={'auto'}
        bg={'white'}
        borderRadius={{ base: '16px', md: '20px' }}
        boxShadow={
          'rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 1px 2px 0px'
        }
      >
        <VStack px={{ base: 4, md: 8 }} py={{ base: isCash ? 4 : 6, md: isCash ? 6 : 8 }}>
          <Box display={'flex'} justifyContent={'space-between'} width={'100%'}>
            <MerchantLogo boxHeight={['35px', '35px', '52px']} boxWidth={['84px', '84px', '125px']} />
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-end"
              wordBreak="break-word"
              overflowWrap="break-word"
            >
              <Text fontWeight={700} maxWidth={350} textAlign={'end'}>{`N. de transacción: ${
                invoices[0]?.paymentId || ''
              }`}</Text>
              <Text fontWeight={700}>
                {`Fecha: `}
                <Text as={'span'} fontWeight={400}>
                  {invoices[0]?.paymentDate || now().format('YYYY-MM-DD')}
                </Text>
              </Text>
            </Box>
          </Box>
          <Box
            display={'flex'}
            flexDirection={'column'}
            alignItems={'center'}
            justifyContent={'center'}
            paddingY={isCash ? 2 : 5}
          >
            {!isCash && (
              <>
                {invoices[0]?.status === INVOICE_STATUS.READY && invoices[0]?.paymentDate ? (
                  <Box boxSize={'90px'} display={'flex'} justifyContent={'center'}>
                    <Spinner size="xl" />
                  </Box>
                ) : (
                  <Image src={icon} boxSize={'90px'} />
                )}
                <Text fontWeight={700}>{approved1}</Text>
                {!invoices[0]?.paymentDate && invoices[0]?.statusMessage && (
                  <Text style={{ textAlign: 'center' }}>{statusMessagesMapping[invoices[0]?.statusMessage]}</Text>
                )}
              </>
            )}

            {!isDeclined && (
              <>
                {!isCash && <Text style={{ textAlign: 'center' }}>{approved2}</Text>}
                {invoices[0]?.brebPaymentDetails && invoices[0].status === INVOICE_STATUS.READY && (
                  <BrebPaymentAwait
                    keyValue={invoices[0].brebPaymentDetails.keyValue}
                    qrValue={invoices[0].brebPaymentDetails.qrValue}
                    amount={totalAmount}
                    showQR={true}
                  />
                )}
                {invoices[0]?.cashPaymentDetails && invoices[0].status === INVOICE_STATUS.READY && (
                  <BancolombiaCashPaymentAwait
                    agreement={invoices[0].cashPaymentDetails.agreement}
                    reference={invoices[0].cashPaymentDetails.reference}
                    barcode={invoices[0].cashPaymentDetails.barcode}
                    amount={totalAmount}
                    customerName={invoices[0].customerName}
                    customerDocumentNumber={invoices[0].customerDocumentNumber}
                    description={description}
                    dueDate={invoices[0].expirationDate}
                  />
                )}
              </>
            )}
          </Box>
          <Text fontWeight={700}>DETALLE DE PAGO</Text>
          <Box width={'100%'} bg={'#F7F9FC'} p={4} borderRadius={'8px'}>
            {tableFields.map((ele, index) => (
              <Box
                display={'flex'}
                justifyContent={'space-between'}
                alignItems={'center'}
                borderBottom={'1px solid #DFDEE2'}
                py={1}
                key={index}
              >
                <Text fontWeight={600}>{ele?.fieldName}</Text>
                <Text maxWidth={350}>{ele?.fieldValue}</Text>
              </Box>
            ))}
          </Box>

          {hasPalommaPlusWin && (
            <Box width={'100%'} bg={'#E8F5E9'} p={4} borderRadius={'8px'} border={'2px solid #4CAF50'} mt={4}>
              <VStack spacing={3} alignItems={'center'}>
                <Text fontWeight={700} fontSize={'lg'} textAlign={'center'}>
                  🏆 PALOMMA PLUS 🏆
                </Text>
                <VStack spacing={2} alignItems={'center'}>
                  <Text textAlign={'center'} fontWeight={600} fontSize={'md'} color={'#2E7D32'}>
                    ¡Felicitaciones!
                  </Text>
                  <Text textAlign={'center'} fontWeight={700} fontSize={'xl'} color={'#1B5E20'}>
                    Ganaste {formatCurrency(totalPalommaPlusRewardAmount)} en Saldo Palomma.
                  </Text>
                  <Text textAlign={'center'} fontSize={'sm'} color={'#424242'} mt={2}>
                    Este saldo ya está disponible en tu cuenta y puedes usarlo para pagar tus próximas facturas o
                    Palomma Plus.
                  </Text>
                </VStack>
              </VStack>
            </Box>
          )}

          <Text textColor={'#9F9DB0'} textAlign={'center'}>
            El extracto se verá reflejado a nombre de “PALOMMA SAS”
          </Text>
          <PalommaDisclaimer />
          {merchant?.receiptFinePrint && (
            <Text color={'#9F9DB0'} textAlign={'center'} fontSize={'8px'}>
              {merchant.receiptFinePrint}
            </Text>
          )}
          <VStack width={'100%'} spacing={2} mt={2}>
            {invoices[0]?.redirectUrl && (
              <Button
                colorScheme="blue"
                size="lg"
                width={'100%'}
                onClick={() => window.location.replace(invoices[0].redirectUrl!)}
              >
                Volver al comercio
              </Button>
            )}
          </VStack>
        </VStack>
      </Box>
    </Skeleton>
  );
};

export default Receipt;
